import { createAuthorizationCommandKeys } from './authorization-command-keys'
import {
  clinicAdoptionSchema,
  type ClinicAdoption,
} from './organization-onboarding-contracts'
import type { z } from 'zod'
import {
  pmsCapabilitySchema,
  type PmsCapabilityResponse,
} from './pms-capability-contracts'

import type { DashboardUser } from '../auth/auth-adapter'
import {
  administratorAlertSchema,
  clinicAlertSchema,
  type AdministratorAlert,
  type AlertSave,
  type ClinicAlert,
} from '../organization-alert/organization-alert-contracts'
import { getEnvironment } from '../../lib/environment'
import {
  bootstrapResponseSchema,
  onboardingResponseSchema,
  provisioningActionResponseSchema,
  provisioningProgressResponseSchema,
  signupIntentResponseSchema,
  type BootstrapResponse,
  type OnboardingResponse,
  type ProvisioningActionResponse,
  type ProvisioningProgressResponse,
} from './onboarding-contracts'
import {
  profileResponseSchema,
  type ProfileDraft,
  type ProfileResponse,
} from './onboarding-profile-contracts'
import {
  nexHealthOnboardingResponseSchema,
  type NexHealthOnboardingResponse,
} from './onboarding-nexhealth-contracts'
import {
  schedulingResponseSchema,
  type SchedulingResponse,
} from './onboarding-scheduling-contracts'
import {
  communicationsResponseSchema,
  type CommunicationsPatch,
  type CommunicationsResponse,
} from './onboarding-communications-contracts'
import {
  reviewResponseSchema,
  type ReviewResponse,
} from './onboarding-review-contracts'
import {
  activationResponseSchema,
  type ActivationResponse,
  validationResponseSchema,
  type ValidationResponse,
} from './onboarding-validation-contracts'
import {
  confirmedOrganizationClinicSchema,
  administratorAccessSchema,
  administratorInvitationSchema,
  claimedInvitationSchema,
  invitationPreviewSchema,
  managerInvitationSchema,
  managerLifecycleResultSchema,
  organizationAccessManagementSchema,
  organizationResponseSchema,
  organizationCommunicationsSchema,
  organizationCommunicationsPrerequisitesSchema,
  twilioConnectionSchema,
  type TwilioConnection,
  organizationCommunicationsRecoverySchema,
  resolvedNexHealthLocationSchema,
  type ConfirmedOrganizationClinic,
  type AdministratorAccess,
  type AdministratorInvitation,
  type ClaimedInvitation,
  type InvitationPreview,
  type ManagerInvitation,
  type ManagerLifecycleResult,
  type OrganizationAccessManagement,
  type OrganizationResponse,
  type OrganizationCommunications,
  type OrganizationCommunicationsPrerequisites,
  type OrganizationCommunicationsRecovery,
  type ResolvedNexHealthLocation,
} from './organization-onboarding-contracts'

export type OnboardingApiErrorCode =
  | 'unauthorized'
  | 'classification-required'
  | 'forbidden'
  | 'invalid-profile'
  | 'revision-conflict'
  | 'invalid-state'
  | 'invalid-nexhealth'
  | 'credential-rejected'
  | 'nexhealth-unavailable'
  | 'operation-in-progress'
  | 'stale-discovery'
  | 'invalid-scheduling'
  | 'invalid-communications'
  | 'invalid-review'
  | 'review-incomplete'
  | 'provisioning-incomplete'
  | 'provisioning-reconciliation'
  | 'provisioning-progress-unavailable'
  | 'invalid-validation'
  | 'validation-unavailable'
  | 'invalid-activation'
  | 'activation-blocked'
  | 'activation-reconciliation'
  | 'organization-conflict'
  | 'communications-setup-blocked'
  | 'communications-provider-unavailable'
  | 'nexhealth-location-inaccessible'
  | 'nexhealth-location-sync-unhealthy'
  | 'nexhealth-location-invalid'
  | 'nexhealth-location-unavailable'
  | 'manager-conflict'
  | 'manager-reconciliation-required'
  | 'reassign-required'
  | 'invitation-expired'
  | 'invitation-email-mismatch'
  | 'invitation-invalid'
  | 'invitation-conflict'
  | 'invitation-delivery-failed'
  | 'invitation-account-unusable'
  | 'client-upgrade-required'
  | 'transport'
  | 'server'
  | 'invalid-response'

export class OnboardingApiError extends Error {
  readonly code: OnboardingApiErrorCode
  readonly status: number | null
  readonly reason: string | null

  constructor(
    code: OnboardingApiErrorCode,
    status: number | null = null,
    reason?: string
  ) {
    super(
      code === 'client-upgrade-required'
        ? 'Refresh this page to use the current authorization service.'
        : code === 'unauthorized'
          ? 'Authentication is required'
          : code === 'classification-required'
            ? 'Principal classification is required'
            : code === 'revision-conflict'
              ? 'Profile data changed elsewhere'
              : code === 'communications-setup-blocked'
                ? reason === 'CLINIC_INFO_AND_WORKING_HOURS_REQUIRED'
                  ? 'Complete Clinic Info, including the clinic email, and Working Hours in Settings before provisioning Communications.'
                  : reason === 'CLINIC_INFO_REQUIRED'
                    ? 'Complete Clinic Info, including the clinic email, in Settings before provisioning Communications.'
                    : reason === 'WORKING_HOURS_REQUIRED'
                      ? 'Complete Working Hours in Settings before provisioning Communications.'
                      : reason === 'NEXHEALTH_WORKSPACE_EVIDENCE_REQUIRED'
                        ? 'Current NexHealth workspace evidence is unavailable. Refresh provider resources before provisioning Communications.'
                        : reason === 'SERVICES_AND_BOOKING_REQUIRED'
                          ? 'Voice requires Services and Booking. In Settings → Services, add a service and mark it Available for Flio booking, or choose Text only.'
                          : reason === 'SERVICES_REQUIRED'
                            ? 'Voice requires at least one available service in Settings → Services, or choose Text only.'
                            : reason === 'BOOKING_REQUIRED'
                              ? 'Voice requires Booking. In Settings → Services, mark at least one service Available for Flio booking, or choose Text only.'
                              : 'The number is unavailable or Communications setup needs attention'
                : code === 'communications-provider-unavailable'
                  ? 'Flio could not verify the provider safely. Please retry.'
                  : 'Onboarding could not be loaded'
    )
    this.name = 'OnboardingApiError'
    this.code = code
    this.status = status
    this.reason = reason ?? null
  }
}

export type OnboardingApiClient = {
  requestSignupIntent: (email: string) => Promise<string>
  bootstrap: (
    user: DashboardUser,
    signupIntent: string
  ) => Promise<BootstrapResponse>
  getStatus: (user: DashboardUser) => Promise<OnboardingResponse>
  getProfile: (user: DashboardUser) => Promise<ProfileResponse>
  saveProfile: (
    user: DashboardUser,
    expectedRevision: number,
    profile: ProfileDraft
  ) => Promise<ProfileResponse>
  completeProfile: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<ProfileResponse>
  pmsCapability?: (
    user: DashboardUser,
    tenantUid: string,
    command?: Record<string, unknown>
  ) => Promise<PmsCapabilityResponse>
  getNexHealth: (user: DashboardUser) => Promise<NexHealthOnboardingResponse>
  connectNexHealth: (
    user: DashboardUser,
    expectedRevision: number,
    subdomain: string,
    credential: string
  ) => Promise<NexHealthOnboardingResponse>
  discoverNexHealth: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<NexHealthOnboardingResponse>
  selectNexHealthLocation: (
    user: DashboardUser,
    expectedRevision: number,
    discoveryRevision: number,
    locationId: number
  ) => Promise<NexHealthOnboardingResponse>
  getScheduling: (user: DashboardUser) => Promise<SchedulingResponse>
  discoverScheduling: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<SchedulingResponse>
  patchScheduling: (
    user: DashboardUser,
    expectedRevision: number,
    discoveryRevision: number,
    patch: {
      bookingConsent: { newAppointment: boolean }
      defaultProviderId?: number
    }
  ) => Promise<SchedulingResponse>
  validateScheduling: (
    user: DashboardUser,
    expectedRevision: number,
    discoveryRevision: number
  ) => Promise<SchedulingResponse>
  completeScheduling: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<SchedulingResponse>
  getCommunications: (user: DashboardUser) => Promise<CommunicationsResponse>
  saveCommunications: (
    user: DashboardUser,
    patch: CommunicationsPatch
  ) => Promise<CommunicationsResponse>
  completeCommunications: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<CommunicationsResponse>
  getReview: (user: DashboardUser) => Promise<ReviewResponse>
  completeReview: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<ReviewResponse>
  startProvisioning: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<ProvisioningActionResponse>
  completeProvisioning: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<ProvisioningActionResponse>
  getProvisioningProgress?: (
    user: DashboardUser
  ) => Promise<ProvisioningProgressResponse>
  getValidation?: (user: DashboardUser) => Promise<ValidationResponse>
  startValidation?: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<ValidationResponse>
  completeActivation?: (
    user: DashboardUser,
    expectedRevision: number
  ) => Promise<ActivationResponse>
  previewClinicAdoption?: (
    user: DashboardUser,
    operationId: string
  ) => Promise<ClinicAdoption>
  adoptExistingClinic?: (
    user: DashboardUser,
    operationId: string,
    approvalFingerprint: string
  ) => Promise<ClinicAdoption>
  getOrganization?: (user: DashboardUser) => Promise<OrganizationResponse>
  getOrganizationAccess?: (
    user: DashboardUser
  ) => Promise<OrganizationAccessManagement>
  createManagerInvitation?: (
    user: DashboardUser,
    input: { email: string; tenantUid: string }
  ) => Promise<ManagerInvitation>
  revokeManagerInvitation?: (
    user: DashboardUser,
    invitationId: string
  ) => Promise<ManagerInvitation>
  grantClinicManager?: (
    user: DashboardUser,
    input: {
      operationId: string
      targetUid: string
      expectedMembershipRevision: number
      tenantUid: string
    }
  ) => Promise<ManagerLifecycleResult>
  revokeClinicManager?: (
    user: DashboardUser,
    input: {
      operationId: string
      targetUid: string
      expectedMembershipRevision: number
      tenantUid: string
    }
  ) => Promise<ManagerLifecycleResult>
  reassignClinicManager?: (
    user: DashboardUser,
    input: {
      operationId: string
      targetUid: string
      expectedMembershipRevision: number
      sourceTenantUid: string
      destinationTenantUid: string
    }
  ) => Promise<ManagerLifecycleResult>
  previewManagerInvitation?: (token: string) => Promise<InvitationPreview>
  claimManagerInvitation?: (
    user: DashboardUser,
    token: string
  ) => Promise<ClaimedInvitation>
  getAdministratorAccess?: (user: DashboardUser) => Promise<AdministratorAccess>
  /** The Organization's alert; Organization Administrators only. */
  getOrganizationAlert?: (user: DashboardUser) => Promise<AdministratorAlert>
  saveOrganizationAlert?: (
    user: DashboardUser,
    input: AlertSave
  ) => Promise<AdministratorAlert>
  /** The active alert of one clinic's Organization, for any of its users. */
  getClinicOrganizationAlert?: (
    user: DashboardUser,
    tenantUid: string
  ) => Promise<ClinicAlert>
  createAdministratorInvitation?: (
    user: DashboardUser,
    input: { email: string }
  ) => Promise<AdministratorInvitation>
  revokeAdministratorInvitation?: (
    user: DashboardUser,
    invitationId: string
  ) => Promise<AdministratorInvitation>
  resolveOrganizationClinicLocation?: (
    user: DashboardUser,
    locationId: number
  ) => Promise<ResolvedNexHealthLocation>
  confirmOrganizationClinic?: (
    user: DashboardUser,
    locationId: number,
    verificationFingerprint: string
  ) => Promise<ConfirmedOrganizationClinic>
  repairOrganizationTwilio?: (
    user: DashboardUser,
    tenantUid: string
  ) => Promise<TwilioConnection>
  verifyOrganizationTwilio?: (
    user: DashboardUser,
    tenantUid: string
  ) => Promise<TwilioConnection>
  getOrganizationCommunications?: (
    user: DashboardUser,
    tenantUid: string
  ) => Promise<OrganizationCommunications>
  getOrganizationCommunicationsPrerequisites?: (
    user: DashboardUser,
    tenantUid: string
  ) => Promise<OrganizationCommunicationsPrerequisites>
  setupOrganizationCommunications?: (
    user: DashboardUser,
    input: {
      tenantUid: string
      expectedRevision: number
      publicNumber: string
      capability: 'TEXT_ONLY' | 'VOICE_ONLY' | 'VOICE_AND_TEXT'
    }
  ) => Promise<OrganizationCommunications>
  advanceOrganizationCommunications?: (
    user: DashboardUser,
    tenantUid: string
  ) => Promise<OrganizationCommunications>
  recoverOrganizationCommunications?: (
    user: DashboardUser,
    input: { tenantUid: string; planId: string }
  ) => Promise<OrganizationCommunicationsRecovery>
}

type ClientOptions = {
  baseUrl: string
  fetchImplementation?: typeof fetch
}

export function createOnboardingApiClient({
  baseUrl,
  fetchImplementation = fetch,
}: ClientOptions): OnboardingApiClient {
  const commandKeys = createAuthorizationCommandKeys()
  async function request<T>(
    path: string,
    schema: z.ZodType<T>,
    init: RequestInit,
    user?: DashboardUser,
    forceTokenRefresh = false
  ): Promise<T> {
    const headers = new Headers(init.headers)
    headers.set('Accept', 'application/json')
    if (init.body !== undefined) headers.set('Content-Type', 'application/json')
    if (user) {
      let token: string
      try {
        token = forceTokenRefresh
          ? await user.getIdToken(true)
          : await user.getIdToken()
      } catch {
        throw new OnboardingApiError('unauthorized')
      }
      if (!token) throw new OnboardingApiError('unauthorized')
      headers.set('Authorization', `Bearer ${token}`)
    }

    const command = await commandKeys.acquire(user?.uid, path, init)
    if (command) headers.set('Idempotency-Key', command.key)
    let response: Response
    try {
      response = await fetchImplementation(
        `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`,
        { ...init, headers }
      )
    } catch {
      throw new OnboardingApiError('transport')
    }

    if (
      response.status >= 400 &&
      response.status < 500 &&
      response.status !== 409
    ) {
      command?.release()
    }
    if (response.status === 426)
      throw new OnboardingApiError('client-upgrade-required', 426)
    if (!response.ok) {
      let machineError: string | undefined
      let machineReason: string | undefined
      try {
        const body: unknown = await response.json()
        if (
          typeof body === 'object' &&
          body !== null &&
          'error' in body &&
          typeof body.error === 'string'
        ) {
          machineError = body.error
        }
        if (
          typeof body === 'object' &&
          body !== null &&
          'reason' in body &&
          typeof body.reason === 'string'
        ) {
          machineReason = body.reason
        }
      } catch {
        // HTTP status remains the bounded fallback authority.
      }
      if (response.status === 401) {
        throw new OnboardingApiError('unauthorized', 401)
      }
      if (
        response.status === 409 &&
        machineError === 'principal_classification_required'
      ) {
        throw new OnboardingApiError('classification-required', 409)
      }
      if (response.status === 409 && machineError === 'revision_conflict') {
        throw new OnboardingApiError('revision-conflict', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'invalid_onboarding_state'
      ) {
        throw new OnboardingApiError('invalid-state', 409)
      }
      if (response.status === 409 && machineError === 'operation_in_progress') {
        throw new OnboardingApiError('operation-in-progress', 409)
      }
      if (response.status === 409 && machineError === 'stale_discovery') {
        throw new OnboardingApiError('stale-discovery', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'stale_scheduling_discovery'
      ) {
        throw new OnboardingApiError('stale-discovery', 409)
      }
      if (response.status === 403) {
        if (machineError === 'invitation_email_mismatch') {
          throw new OnboardingApiError('invitation-email-mismatch', 403)
        }
        throw new OnboardingApiError('forbidden', 403)
      }
      if (
        response.status === 409 &&
        machineError === 'manager_reconciliation_required'
      ) {
        throw new OnboardingApiError('manager-reconciliation-required', 409)
      }
      if (response.status === 409 && machineError === 'reassign_required') {
        throw new OnboardingApiError('reassign-required', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'manager_lifecycle_conflict'
      ) {
        throw new OnboardingApiError('manager-conflict', 409)
      }
      if (response.status === 409 && machineError === 'invitation_expired') {
        throw new OnboardingApiError('invitation-expired', 409)
      }
      if (response.status === 409 && machineError === 'invitation_invalid') {
        throw new OnboardingApiError('invitation-invalid', 409)
      }
      if (response.status === 409 && machineError === 'invitation_conflict') {
        throw new OnboardingApiError('invitation-conflict', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'invitation_account_unusable'
      ) {
        throw new OnboardingApiError('invitation-account-unusable', 409)
      }
      if (
        response.status === 503 &&
        machineError === 'invitation_delivery_failed'
      ) {
        throw new OnboardingApiError('invitation-delivery-failed', 503)
      }
      if (
        response.status === 409 &&
        machineError === 'organization_onboarding_conflict'
      ) {
        throw new OnboardingApiError('organization-conflict', 409)
      }
      if (
        response.status === 409 &&
        [
          'communications_setup_blocked',
          'communications_setup_conflict',
          'communications_provider_rejected',
        ].includes(machineError ?? '')
      ) {
        throw new OnboardingApiError(
          'communications-setup-blocked',
          409,
          machineReason
        )
      }
      if (
        response.status === 503 &&
        [
          'communications_provider_unavailable',
          'communications_provider_rejected',
        ].includes(machineError ?? '')
      ) {
        throw new OnboardingApiError('communications-provider-unavailable', 503)
      }
      if (
        (response.status === 404 || response.status === 422) &&
        machineError === 'nexhealth_location_not_accessible'
      ) {
        throw new OnboardingApiError(
          'nexhealth-location-inaccessible',
          response.status
        )
      }
      if (
        response.status === 409 &&
        machineError === 'nexhealth_location_sync_unhealthy'
      ) {
        throw new OnboardingApiError('nexhealth-location-sync-unhealthy', 409)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_nexhealth_location_id'
      ) {
        throw new OnboardingApiError('nexhealth-location-invalid', 400)
      }
      if (
        response.status === 503 &&
        machineError === 'nexhealth_location_verification_unavailable'
      ) {
        throw new OnboardingApiError('nexhealth-location-unavailable', 503)
      }
      if (response.status === 400 && machineError === 'invalid_profile') {
        throw new OnboardingApiError('invalid-profile', 400)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_nexhealth_connection'
      ) {
        throw new OnboardingApiError('invalid-nexhealth', 400)
      }
      if (response.status === 422 && machineError === 'credential_rejected') {
        throw new OnboardingApiError('credential-rejected', 422)
      }
      if (response.status === 503 && machineError === 'nexhealth_unavailable') {
        throw new OnboardingApiError('nexhealth-unavailable', 503)
      }
      if (
        response.status === 503 &&
        machineError === 'scheduling_vendor_unavailable'
      ) {
        throw new OnboardingApiError('nexhealth-unavailable', 503)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_scheduling_request'
      ) {
        throw new OnboardingApiError('invalid-scheduling', 400)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_communications_request'
      ) {
        throw new OnboardingApiError('invalid-communications', 400)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_review_request'
      ) {
        throw new OnboardingApiError('invalid-review', 400)
      }
      if (response.status === 409 && machineError === 'review_incomplete') {
        throw new OnboardingApiError('review-incomplete', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'provisioning_incomplete'
      ) {
        throw new OnboardingApiError('provisioning-incomplete', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'provisioning_reconciliation_required'
      ) {
        throw new OnboardingApiError('provisioning-reconciliation', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'provisioning_progress_unavailable'
      ) {
        throw new OnboardingApiError('provisioning-progress-unavailable', 409)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_validation_request'
      ) {
        throw new OnboardingApiError('invalid-validation', 400)
      }
      if (
        response.status === 409 &&
        machineError === 'validation_unavailable'
      ) {
        throw new OnboardingApiError('validation-unavailable', 409)
      }
      if (
        response.status === 400 &&
        machineError === 'invalid_activation_request'
      ) {
        throw new OnboardingApiError('invalid-activation', 400)
      }
      if (response.status === 409 && machineError === 'activation_blocked') {
        throw new OnboardingApiError('activation-blocked', 409)
      }
      if (
        response.status === 409 &&
        machineError === 'activation_reconciliation_required'
      ) {
        throw new OnboardingApiError('activation-reconciliation', 409)
      }
      throw new OnboardingApiError('server', response.status)
    }

    let payload: unknown
    try {
      payload = await response.json()
    } catch {
      throw new OnboardingApiError('invalid-response', response.status)
    }
    const parsed = schema.safeParse(payload)
    if (!parsed.success) throw new OnboardingApiError('invalid-response')
    command?.release()
    return parsed.data
  }

  return {
    async requestSignupIntent(email) {
      const response = await request(
        '/v1/signup-intents',
        signupIntentResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ email }),
        }
      )
      return response.signupIntent
    },
    bootstrap(user, signupIntent) {
      return request(
        '/v1/onboarding/bootstrap',
        bootstrapResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ signupIntent }),
        },
        user
      )
    },
    getStatus(user) {
      return request(
        '/v1/onboarding',
        onboardingResponseSchema,
        { method: 'GET' },
        user
      )
    },
    getProfile(user) {
      return request(
        '/v1/onboarding/profile',
        profileResponseSchema,
        { method: 'GET' },
        user
      )
    },
    saveProfile(user, expectedRevision, profile) {
      return request(
        '/v1/onboarding/profile',
        profileResponseSchema,
        {
          method: 'PATCH',
          body: JSON.stringify({ expectedRevision, ...profile }),
        },
        user
      )
    },
    completeProfile(user, expectedRevision) {
      return request(
        '/v1/onboarding/profile/complete',
        profileResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    pmsCapability(user, tenantUid, command) {
      return request(
        '/v1/onboarding/pms-capability',
        pmsCapabilitySchema,
        {
          method: command ? 'PUT' : 'POST',
          body: JSON.stringify({ tenantUid, ...command }),
        },
        user
      )
    },
    getNexHealth(user) {
      return request(
        '/v1/onboarding/nexhealth',
        nexHealthOnboardingResponseSchema,
        { method: 'GET' },
        user
      )
    },
    connectNexHealth(user, expectedRevision, subdomain, credential) {
      return request(
        '/v1/onboarding/nexhealth/credential',
        nexHealthOnboardingResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision, subdomain, credential }),
        },
        user
      )
    },
    discoverNexHealth(user, expectedRevision) {
      return request(
        '/v1/onboarding/nexhealth/discover',
        nexHealthOnboardingResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    selectNexHealthLocation(
      user,
      expectedRevision,
      discoveryRevision,
      locationId
    ) {
      return request(
        '/v1/onboarding/nexhealth/location',
        nexHealthOnboardingResponseSchema,
        {
          method: 'PUT',
          body: JSON.stringify({
            expectedRevision,
            discoveryRevision,
            locationId,
          }),
        },
        user
      )
    },
    getScheduling(user) {
      return request(
        '/v1/onboarding/scheduling',
        schedulingResponseSchema,
        { method: 'GET' },
        user
      )
    },
    discoverScheduling(user, expectedRevision) {
      return request(
        '/v1/onboarding/scheduling/discover',
        schedulingResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    patchScheduling(user, expectedRevision, discoveryRevision, patch) {
      return request(
        '/v1/onboarding/scheduling',
        schedulingResponseSchema,
        {
          method: 'PATCH',
          body: JSON.stringify({
            expectedRevision,
            discoveryRevision,
            ...patch,
          }),
        },
        user
      )
    },
    validateScheduling(user, expectedRevision, discoveryRevision) {
      return request(
        '/v1/onboarding/scheduling/validate',
        schedulingResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision, discoveryRevision }),
        },
        user
      )
    },
    completeScheduling(user, expectedRevision) {
      return request(
        '/v1/onboarding/scheduling/complete',
        schedulingResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    getCommunications(user) {
      return request(
        '/v1/onboarding/communications',
        communicationsResponseSchema,
        { method: 'GET' },
        user
      )
    },
    saveCommunications(user, patch) {
      return request(
        '/v1/onboarding/communications',
        communicationsResponseSchema,
        {
          method: 'PATCH',
          body: JSON.stringify(patch),
        },
        user
      )
    },
    completeCommunications(user, expectedRevision) {
      return request(
        '/v1/onboarding/communications/complete',
        communicationsResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    getReview(user) {
      return request(
        '/v1/onboarding/review',
        reviewResponseSchema,
        { method: 'GET' },
        user
      )
    },
    completeReview(user, expectedRevision) {
      return request(
        '/v1/onboarding/review/complete',
        reviewResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    startProvisioning(user, expectedRevision) {
      return request(
        '/v1/onboarding/provisioning/start',
        provisioningActionResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    completeProvisioning(user, expectedRevision) {
      return request(
        '/v1/onboarding/provisioning/complete',
        provisioningActionResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    getProvisioningProgress(user) {
      return request(
        '/v1/onboarding/provisioning/progress',
        provisioningProgressResponseSchema,
        { method: 'GET' },
        user
      )
    },
    getValidation(user) {
      return request(
        '/v1/onboarding/validation',
        validationResponseSchema,
        { method: 'GET' },
        user
      )
    },
    startValidation(user, expectedRevision) {
      return request(
        '/v1/onboarding/validation/start',
        validationResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    completeActivation(user, expectedRevision) {
      return request(
        '/v1/onboarding/activation/complete',
        activationResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ expectedRevision }),
        },
        user
      )
    },
    previewClinicAdoption(user, operationId) {
      return request(
        '/v1/organization/existing-clinics/preview',
        clinicAdoptionSchema,
        { method: 'POST', body: JSON.stringify({ operationId }) },
        user
      )
    },
    adoptExistingClinic(user, operationId, approvalFingerprint) {
      return request(
        '/v1/organization/existing-clinics/adopt',
        clinicAdoptionSchema,
        {
          method: 'POST',
          body: JSON.stringify({ operationId, approvalFingerprint }),
        },
        user
      )
    },
    getOrganization(user) {
      return request(
        '/v1/organization',
        organizationResponseSchema,
        { method: 'GET' },
        user
      )
    },
    getOrganizationAccess(user) {
      return request(
        '/v1/organization/access-management',
        organizationAccessManagementSchema,
        { method: 'GET' },
        user
      )
    },
    createManagerInvitation(user, input) {
      return request(
        '/v1/organization/clinic-manager-invitations',
        managerInvitationSchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
    revokeManagerInvitation(user, invitationId) {
      return request(
        '/v1/organization/clinic-manager-invitations/revoke',
        managerInvitationSchema,
        {
          method: 'POST',
          body: JSON.stringify({ invitationId }),
        },
        user
      )
    },
    grantClinicManager(user, input) {
      return request(
        '/v1/organization/clinic-managers/grant',
        managerLifecycleResultSchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
    revokeClinicManager(user, input) {
      return request(
        '/v1/organization/clinic-managers/revoke',
        managerLifecycleResultSchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
    reassignClinicManager(user, input) {
      return request(
        '/v1/organization/clinic-managers/reassign',
        managerLifecycleResultSchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
    previewManagerInvitation(token) {
      return request(
        '/v1/clinic-manager-invitations/preview',
        invitationPreviewSchema,
        { method: 'POST', body: JSON.stringify({ token, accountSetup: true }) }
      )
    },
    claimManagerInvitation(user, token) {
      return request(
        '/v1/clinic-manager-invitations/claim',
        claimedInvitationSchema,
        { method: 'POST', body: JSON.stringify({ token }) },
        user,
        true
      )
    },
    getAdministratorAccess(user) {
      return request(
        '/v1/organization/administrator-access',
        administratorAccessSchema,
        { method: 'GET' },
        user
      )
    },
    getOrganizationAlert(user) {
      return request(
        '/v1/organization/alert',
        administratorAlertSchema,
        { method: 'GET' },
        user
      )
    },
    saveOrganizationAlert(user, input) {
      return request(
        '/v1/organization/alert',
        administratorAlertSchema,
        { method: 'PUT', body: JSON.stringify(input) },
        user
      )
    },
    getClinicOrganizationAlert(user, tenantUid) {
      return request(
        '/v1/settings/organization-alert',
        clinicAlertSchema,
        { method: 'POST', body: JSON.stringify({ tenantUid }) },
        user
      )
    },
    createAdministratorInvitation(user, input) {
      return request(
        '/v1/organization/administrator-invitations',
        administratorInvitationSchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
    revokeAdministratorInvitation(user, invitationId) {
      return request(
        '/v1/organization/administrator-invitations/revoke',
        administratorInvitationSchema,
        { method: 'POST', body: JSON.stringify({ invitationId }) },
        user
      )
    },
    resolveOrganizationClinicLocation(user, locationId) {
      return request(
        '/v1/organization/clinic-onboarding/nexhealth/location/resolve',
        resolvedNexHealthLocationSchema,
        {
          method: 'POST',
          body: JSON.stringify({ locationId }),
        },
        user
      )
    },
    confirmOrganizationClinic(user, locationId, verificationFingerprint) {
      return request(
        '/v1/organization/clinic-onboarding/confirm',
        confirmedOrganizationClinicSchema,
        {
          method: 'POST',
          body: JSON.stringify({ locationId, verificationFingerprint }),
        },
        user
      )
    },
    repairOrganizationTwilio(user, tenantUid) {
      return request(
        '/v1/organization/communications/repair',
        twilioConnectionSchema,
        { method: 'POST', body: JSON.stringify({ tenantUid }) },
        user
      )
    },
    verifyOrganizationTwilio(user, tenantUid) {
      return request(
        '/v1/organization/communications/verify',
        twilioConnectionSchema,
        { method: 'POST', body: JSON.stringify({ tenantUid }) },
        user
      )
    },
    getOrganizationCommunications(user, tenantUid) {
      return request(
        '/v1/organization/communications/read',
        organizationCommunicationsSchema,
        { method: 'POST', body: JSON.stringify({ tenantUid }) },
        user
      )
    },
    getOrganizationCommunicationsPrerequisites(user, tenantUid) {
      return request(
        '/v1/organization/communications/prerequisites',
        organizationCommunicationsPrerequisitesSchema,
        { method: 'POST', body: JSON.stringify({ tenantUid }) },
        user
      )
    },
    setupOrganizationCommunications(user, input) {
      return request(
        '/v1/organization/communications/setup',
        organizationCommunicationsSchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
    advanceOrganizationCommunications(user, tenantUid) {
      return request(
        '/v1/organization/communications/advance',
        organizationCommunicationsSchema,
        { method: 'POST', body: JSON.stringify({ tenantUid }) },
        user
      )
    },
    recoverOrganizationCommunications(user, input) {
      return request(
        '/v1/organization/communications/recovery',
        organizationCommunicationsRecoverySchema,
        { method: 'POST', body: JSON.stringify(input) },
        user
      )
    },
  }
}

let runtimeClient: OnboardingApiClient | undefined

export function getOnboardingApiClient(): OnboardingApiClient {
  runtimeClient ??= createOnboardingApiClient({
    baseUrl: getEnvironment().onboardingApiBaseUrl,
  })
  return runtimeClient
}
